import React, { useEffect, useState } from "react";
import axios from "../api/Axios";
import { 
  Table, 
  Badge, 
  Button, 
  Modal, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  Form, 
  FormGroup, 
  Label, 
  Input,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Alert,
  Card,
  CardBody
} from "reactstrap";
import usePermissions from "../hooks/usePermissions";

const VoucherBookingsTable = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  
  // Get permissions
  const { permissions } = usePermissions();
  const canChangeBookings = permissions.some(p => p.codename === 'change_voucherbooking');

  // Fetch bookings on component mount
  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/accounts/voucher-bookings/");
      const bookingsData = response.data.results || response.data || [];
      setBookings(bookingsData);
      setError(null);
    } catch (err) {
      console.error("Error fetching bookings:", err);
      setError(`Failed to load bookings: ${err.response?.status || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle booking acceptance
  const handleAcceptBooking = async (bookingId, reviewNotes = '') => {
    try {
      setUpdating(true);
      await axios.post(`/api/accounts/voucher-bookings/${bookingId}/accept/`, {
        review_notes: reviewNotes
      });
      
      await fetchBookings();
      setShowModal(false);
      setSelectedBooking(null);
      setReviewNotes('');
      alert('Booking approved successfully!');
    } catch (err) {
      console.error("Error accepting booking:", err);
      alert(`Error approving booking: ${err.response?.data?.error || err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  // Handle booking rejection
  const handleRejectBooking = async (bookingId, reviewNotes) => {
    if (!reviewNotes || reviewNotes.trim() === '') {
      alert('Please provide a reason for rejecting this booking.');
      return;
    }
    
    try {
      setUpdating(true);
      await axios.post(`/api/accounts/voucher-bookings/${bookingId}/reject/`, {
        review_notes: reviewNotes
      });
      
      await fetchBookings();
      setShowModal(false);
      setSelectedBooking(null);
      setReviewNotes('');
      alert('Booking rejected successfully!');
    } catch (err) {
      console.error("Error rejecting booking:", err);
      alert(`Error rejecting booking: ${err.response?.data?.error || err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  // Handle BMO contract acceptance
  const handleBMOAcceptContract = async (bookingId) => {
    if (!window.confirm('Are you sure you want to accept this contract? This will confirm the booking.')) {
      return;
    }
    
    try {
      setUpdating(true);
      await axios.post(`/api/accounts/voucher-bookings/${bookingId}/bmo-accept-contract/`);
      
      await fetchBookings();
      setShowModal(false);
      setSelectedBooking(null);
      alert('Contract accepted successfully! Booking is now confirmed.');
    } catch (err) {
      console.error("Error accepting contract:", err);
      alert(`Error accepting contract: ${err.response?.data?.error || err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  // Handle BMO contract rejection
  const handleBMORejectContract = async (bookingId, reviewNotes) => {
    if (!reviewNotes || reviewNotes.trim() === '') {
      alert('Please provide a reason for rejecting this contract.');
      return;
    }
    
    if (!window.confirm('Are you sure you want to reject this contract? This action cannot be undone.')) {
      return;
    }
    
    try {
      setUpdating(true);
      await axios.post(`/api/accounts/voucher-bookings/${bookingId}/bmo-reject-contract/`, {
        review_notes: reviewNotes
      });
      
      await fetchBookings();
      setShowModal(false);
      setSelectedBooking(null);
      setReviewNotes('');
      alert('Contract rejected successfully.');
    } catch (err) {
      console.error("Error rejecting contract:", err);
      alert(`Error rejecting contract: ${err.response?.data?.error || err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  // Handle view contract PDF
  const handleViewContract = (booking) => {
    if (!booking.contract_pdf_path) {
      alert('Contract PDF is not available for this booking.');
      return;
    }
    
    // Construct backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : 'https://lafunza.com');
    const baseUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
    
    // Remove leading slash from path if present
    const pdfPath = booking.contract_pdf_path.startsWith('/') ? booking.contract_pdf_path : `/${booking.contract_pdf_path}`;
    
    // Open PDF in new tab
    const pdfUrl = `${baseUrl}${pdfPath}`;
    console.log('Opening PDF:', pdfUrl);
    window.open(pdfUrl, '_blank');
  };

  // Open review modal
  const openReviewModal = (booking) => {
    setSelectedBooking(booking);
    setReviewNotes('');
    setShowModal(true);
  };

  // Close review modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedBooking(null);
    setReviewNotes('');
  };

  // Format date for display (DD/MM/YYYY)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Format datetime for display
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge configuration
  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'warning', text: 'Pending BMO Approval' },
      'approved': { color: 'success', text: 'Approved' },
      'rejected': { color: 'danger', text: 'Rejected' },
      'cancelled': { color: 'secondary', text: 'Cancelled' },
      'completed': { color: 'info', text: 'Completed' },
      'pending student contract acceptance': { color: 'info', text: 'Pending Student' },
      'pending provider contract acceptance': { color: 'info', text: 'Pending Provider' },
      'pending bmo contract acceptance': { color: 'warning', text: 'Pending BMO Contract' },
      'confirmed': { color: 'success', text: 'Confirmed' }
    };
    
    const config = statusConfig[status] || { color: 'secondary', text: status };
    return <Badge color={config.color} pill>{config.text}</Badge>;
  };

  if (loading) {
    return (
      <Card>
        <CardBody>
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading bookings...</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert color="danger">
        <i className="bi bi-exclamation-triangle-fill me-2"></i>
        {error}
        <Button 
          color="link" 
          size="sm" 
          onClick={fetchBookings}
          className="ms-2 text-danger"
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <>
      {/* Header with summary and refresh */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <span className="text-muted">Total Bookings: </span>
          <Badge color="success" pill className="ms-2">{bookings.length}</Badge>
        </div>
        <Button 
          color="outline-primary" 
          size="sm" 
          onClick={fetchBookings}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </Button>
      </div>

      {bookings.length === 0 ? (
        <Alert color="info">
          <i className="bi bi-info-circle me-2"></i>
          No voucher bookings found. Bookings will appear here once students submit requests.
        </Alert>
      ) : (
        <div style={{ minHeight: '500px' }}>
          <div className="table-responsive">
            <Table striped hover style={{ minHeight: '450px' }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Student</th>
                  <th>Property</th>
                  <th>Voucher</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => (
                  <tr key={booking.id}>
                    <td><strong>{booking.id}</strong></td>
                    <td>
                      <div>
                        <strong>{booking.student_name || '-'}</strong>
                        <br />
                        <small className="text-muted">{booking.student_email || '-'}</small>
                      </div>
                    </td>
                    <td>
                      <strong>{booking.property_name || '-'}</strong>
                    </td>
                    <td>
                      <Badge color="info" pill>
                        {booking.voucher_code || '-'}
                      </Badge>
                    </td>
                    <td>{formatDate(booking.check_in_date)}</td>
                    <td>{formatDate(booking.check_out_date)}</td>
                    <td>{getStatusBadge(booking.booking_status)}</td>
                    <td>
                      <small>{formatDateTime(booking.created_at)}</small>
                    </td>
                    <td className="text-center">
                      <UncontrolledDropdown>
                        <DropdownToggle color="outline-primary" size="sm">
                          ⋯
                        </DropdownToggle>
                        <DropdownMenu end>
                          <DropdownItem onClick={() => openReviewModal(booking)}>
                            <i className="bi bi-eye me-2"></i>
                            View Details
                          </DropdownItem>
                          {booking.contract_pdf_path && (
                            <DropdownItem onClick={() => handleViewContract(booking)}>
                              <i className="bi bi-file-pdf me-2"></i>
                              View Contract PDF
                            </DropdownItem>
                          )}
                          {booking.booking_status === 'pending' && canChangeBookings && (
                            <>
                              <DropdownItem divider />
                              <DropdownItem 
                                onClick={() => openReviewModal(booking)}
                                className="text-success"
                              >
                                <i className="bi bi-check-lg me-2"></i>
                                Approve Booking
                              </DropdownItem>
                              <DropdownItem 
                                onClick={() => openReviewModal(booking)}
                                className="text-danger"
                              >
                                <i className="bi bi-x-lg me-2"></i>
                                Reject Booking
                              </DropdownItem>
                            </>
                          )}
                          {booking.booking_status === 'pending bmo contract acceptance' && canChangeBookings && (
                            <>
                              <DropdownItem divider />
                              <DropdownItem 
                                onClick={() => openReviewModal(booking)}
                                className="text-success"
                              >
                                <i className="bi bi-check-circle me-2"></i>
                                Accept Contract
                              </DropdownItem>
                              <DropdownItem 
                                onClick={() => openReviewModal(booking)}
                                className="text-danger"
                              >
                                <i className="bi bi-x-circle me-2"></i>
                                Reject Contract
                              </DropdownItem>
                            </>
                          )}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {/* Review Modal */}
      <Modal isOpen={showModal} toggle={closeModal} size="lg">
        <ModalHeader toggle={closeModal}>
          <i className="bi bi-calendar-check me-2"></i>
          Booking Details - #{selectedBooking?.id}
        </ModalHeader>
        <ModalBody>
          {selectedBooking && (
            <div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Student:</strong>
                  <div>{selectedBooking.student_name}</div>
                  <small className="text-muted">{selectedBooking.student_email}</small>
                </div>
                <div className="col-md-6">
                  <strong>Status:</strong>
                  <div className="mt-1">
                    {getStatusBadge(selectedBooking.booking_status)}
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Property:</strong>
                  <div>{selectedBooking.property_name}</div>
                  <small className="text-muted">{selectedBooking.property_address}</small>
                </div>
                <div className="col-md-6">
                  <strong>Voucher Code:</strong>
                  <div><code>{selectedBooking.voucher_code}</code></div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4">
                  <strong>Check-in:</strong>
                  <div>{formatDate(selectedBooking.check_in_date)}</div>
                </div>
                <div className="col-md-4">
                  <strong>Check-out:</strong>
                  <div>{formatDate(selectedBooking.check_out_date)}</div>
                </div>
                <div className="col-md-4">
                  <strong>Duration:</strong>
                  <div>{selectedBooking.booking_duration_days} days</div>
                </div>
              </div>

              {selectedBooking.special_requests && (
                <div className="mb-3">
                  <strong>Special Requests:</strong>
                  <div className="bg-light p-2 rounded mt-1">
                    {selectedBooking.special_requests}
                  </div>
                </div>
              )}

              {selectedBooking.review_notes && (
                <div className="mb-3">
                  <strong>Previous Review Notes:</strong>
                  <div className="bg-light p-2 rounded mt-1">
                    {selectedBooking.review_notes}
                  </div>
                </div>
              )}

              {selectedBooking.reviewed_by_name && (
                <div className="mb-3">
                  <strong>Reviewed by:</strong> {selectedBooking.reviewed_by_name}
                  <br />
                  <strong>Review date:</strong> {formatDateTime(selectedBooking.review_date)}
                </div>
              )}

              {(selectedBooking.booking_status === 'pending' || selectedBooking.booking_status === 'pending bmo contract acceptance') && canChangeBookings && (
                <Form>
                  <FormGroup>
                    <Label for="reviewNotes">
                      {selectedBooking.booking_status === 'pending bmo contract acceptance' ? 'Rejection Notes (required for rejection)' : 'Review Notes'}
                    </Label>
                    <Input
                      type="textarea"
                      id="reviewNotes"
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder={selectedBooking.booking_status === 'pending bmo contract acceptance' 
                        ? 'Provide a reason if rejecting the contract...' 
                        : 'Add notes about this booking review...'}
                      rows="3"
                    />
                  </FormGroup>
                </Form>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {selectedBooking?.contract_pdf_path && (
            <Button
              color="info"
              onClick={() => handleViewContract(selectedBooking)}
              className="me-auto"
            >
              <i className="bi bi-file-pdf me-1"></i>
              View Contract PDF
            </Button>
          )}
          {selectedBooking?.booking_status === 'pending' && canChangeBookings ? (
            <>
              <Button
                color="success"
                onClick={() => handleAcceptBooking(selectedBooking.id, reviewNotes)}
                disabled={updating}
              >
                {updating ? 'Updating...' : 'Approve'}
              </Button>
              <Button
                color="danger"
                onClick={() => handleRejectBooking(selectedBooking.id, reviewNotes)}
                disabled={updating}
              >
                {updating ? 'Updating...' : 'Reject'}
              </Button>
            </>
          ) : selectedBooking?.booking_status === 'pending bmo contract acceptance' && canChangeBookings ? (
            <>
              <Button
                color="success"
                onClick={() => handleBMOAcceptContract(selectedBooking.id)}
                disabled={updating}
              >
                {updating ? 'Updating...' : 'Accept Contract'}
              </Button>
              <Button
                color="danger"
                onClick={() => handleBMORejectContract(selectedBooking.id, reviewNotes)}
                disabled={updating || !reviewNotes.trim()}
              >
                {updating ? 'Updating...' : 'Reject Contract'}
              </Button>
            </>
          ) : selectedBooking?.booking_status === 'pending' && !canChangeBookings ? (
            <div className="text-muted">
              <i className="bi bi-lock-fill me-1"></i>
              You don't have permission to approve or reject bookings.
            </div>
          ) : selectedBooking?.booking_status === 'pending bmo contract acceptance' && !canChangeBookings ? (
            <div className="text-muted">
              <i className="bi bi-lock-fill me-1"></i>
              You don't have permission to accept or reject contracts.
            </div>
          ) : (
            <div className="text-muted">
              This booking has already been reviewed.
            </div>
          )}
          <Button color="secondary" onClick={closeModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default VoucherBookingsTable;
